/**
 * SignInPrompt Component
 *
 * Displays a prompt for unauthenticated users to sign in
 * Uses AuthManager for authentication instead of Auth0 React SDK
 */

import React, { useState } from 'react';
import type { AuthManager } from '../../lib/auth-manager';

export interface SignInPromptProps {
  /**
   * AuthManager instance for authentication
   * Optional until full integration - if not provided, sign-in button will log a warning
   */
  authManager?: AuthManager;
  /**
   * Optional custom message to display
   */
  message?: string;
  /**
   * Optional callback when user initiates sign in
   */
  onSignInClick?: () => void;
  /**
   * Show compact version (for inline use)
   */
  compact?: boolean;
  /**
   * Loading state (e.g., during authentication)
   */
  isLoading?: boolean;
}

/**
 * SignInPrompt - Prompts users to sign in to access commenting features
 *
 * Uses AuthManager to initiate Auth0 login flow through callback handler service.
 */
export const SignInPrompt: React.FC<SignInPromptProps> = ({
  authManager,
  message = 'Sign in to leave comments',
  onSignInClick,
  compact = false,
  isLoading = false,
}) => {
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = () => {
    // Call custom callback if provided
    if (onSignInClick) {
      onSignInClick();
    }

    // Trigger Auth0 login (redirects to Auth0 hosted login page via callback service)
    if (!authManager) {
      console.warn('[SignInPrompt] AuthManager not configured. Cannot initiate login.');
      return;
    }

    try {
      setIsSigningIn(true);
      authManager.login();
      // Note: login() redirects, so code after this won't execute
    } catch (error) {
      console.error('Failed to initiate login:', error);
      setIsSigningIn(false);
    }
  };

  // Don't show anything if already authenticated
  if (authManager?.isAuthenticated()) {
    return null;
  }

  const loading = isLoading || isSigningIn;
  const buttonText = loading ? 'Signing in...' : 'Sign In';
  const fullButtonText = loading ? 'Signing in...' : 'Sign In with Pantheon';

  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '14px',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M10 10a3 3 0 100-6 3 3 0 000 6zM10 12a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            fill="currentColor"
            opacity="0.5"
          />
        </svg>
        <span style={{ flex: 1, color: '#495057' }}>{message}</span>
        <button
          onClick={handleSignIn}
          disabled={loading}
          style={{
            padding: '6px 16px',
            background: loading ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
            opacity: loading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#0056b3';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#007bff';
            }
          }}
        >
          {buttonText}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        textAlign: 'center',
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
      }}
    >
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ marginBottom: '24px', opacity: 0.5 }}
      >
        <circle cx="32" cy="20" r="12" fill="currentColor" />
        <path
          d="M32 36c-13.255 0-24 8.954-24 20h48c0-11.046-10.745-20-24-20z"
          fill="currentColor"
        />
      </svg>

      <h3
        style={{
          margin: '0 0 8px 0',
          fontSize: '20px',
          fontWeight: 600,
          color: '#212529',
        }}
      >
        {message}
      </h3>

      <p
        style={{
          margin: '0 0 24px 0',
          fontSize: '14px',
          color: '#6c757d',
          maxWidth: '320px',
        }}
      >
        Sign in with your Pantheon account to add comments, tag team members, and collaborate on this Multidev environment.
      </p>

      <button
        onClick={handleSignIn}
        disabled={loading}
        style={{
          padding: '12px 32px',
          background: loading ? '#6c757d' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '16px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          boxShadow: loading ? 'none' : '0 2px 4px rgba(0,123,255,0.2)',
          opacity: loading ? 0.7 : 1,
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.currentTarget.style.background = '#0056b3';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.currentTarget.style.background = '#007bff';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,123,255,0.2)';
          }
        }}
      >
        {fullButtonText}
      </button>

      <p
        style={{
          margin: '16px 0 0 0',
          fontSize: '12px',
          color: '#adb5bd',
        }}
      >
        Powered by Auth0
      </p>
    </div>
  );
};
